// src/pages/Portal.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Outlet } from "react-router-dom";
import "./Portal.css";
import { niceMoney, niceASP, nicePercent, formatNumber } from "../configs/formatConfigs";

const API_BASE = process.env.REACT_APP_API_BASE_URL; // REACT_APP_API_BASE_URL is defined in Render.com

const Portal = () => {
  const { user, getAccessTokenSilently } = useAuth0();

  const [files, setFiles] = useState([]);
  const [metricsPayload, setMetricsPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);


  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://ammenconsulting.com/api",
        },
      });

      const cacheBuster = Date.now();
      const reqOpts = {
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      };

      // 1) Get file list
      const filesRes = await fetch(`${API_BASE}/api/files?ts=${cacheBuster}`, reqOpts);
      if (!filesRes.ok) {
        const text = await filesRes.text();
        throw new Error(`Files API error ${filesRes.status}: ${text || filesRes.statusText}`);
      }

      const filesJson = await filesRes.json();
      setFiles(filesJson.files || []);

      // 2) Get portal metrics
      const metricsRes = await fetch(`${API_BASE}/api/metrics/portal?ts=${cacheBuster}`, reqOpts);
      if (!metricsRes.ok) {
        const text = await metricsRes.text();
        throw new Error(`Metrics API error ${metricsRes.status}: ${text || metricsRes.statusText}`);
      }

      const metricsJson = await metricsRes.json();
      // console.log("metrics API payload:", metricsJson);
      // console.log("frontend salesAccess:", metricsJson.salesAccess);
      // console.log("frontend permissions:", metricsJson.permissions);
      setMetricsPayload(metricsJson);
      setLastRefreshedAt(new Date().toISOString());
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh metrics every minute
  // useEffect(() => {
  //   const intervalId = window.setInterval(fetchData, 60000);
  //   return () => window.clearInterval(intervalId);
  // }, [fetchData]);

  // Extracting parts of metrics payload for easier access + console.log for debugging
  console.log("metricsPayload:", metricsPayload); // entire json object from API response
  const metricsServerData = metricsPayload?.metrics ?? null;
  const sourceFile = metricsPayload?.sourceFile ?? null;
  const companyId = metricsPayload?.companyId ?? (user && user["https://ammenconsulting.com/companyId"]);
  const displayName = metricsPayload?.displayName ?? companyId ?? null;
  const last9MonthsSalesObject = metricsServerData?.top100Last9Months ?? null;
  const salesQtyByMonthHistoricallyObject = metricsServerData?.salesQtyByMonthHistorically ?? null;
  const mainDashboardObject = metricsServerData?.mainDashboard ?? null;
  const vantisObject = metricsServerData?.vantis ?? null;

  const salesAccess = metricsPayload?.salesAccess ?? null;

  const lastRefreshedLabel = lastRefreshedAt
    ? new Date(lastRefreshedAt).toLocaleTimeString("en-US")
    : "Never";

  return (
    <main style={{ padding: "4rem 1rem", textAlign: "center" }}>
      <h1>{displayName || companyId} Metrics Portal</h1>
      <div style={{ marginBottom: "1rem" }}>
        <button type="button" onClick={fetchData} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh data"}
        </button>
        <p style={{ margin: "0.5rem 0 0", fontSize: "0.9rem" }}>
          Last refresh: {lastRefreshedLabel}
        </p>
      </div>

      {/* Child routes data renders right here */}
      <Outlet
        context={{
          user,
          files,
          last9MonthsSalesObject,
          salesQtyByMonthHistoricallyObject,
          mainDashboardObject,
          vantisObject,
          metricsServerData,
          sourceFile,
          companyId,
          displayName,
          salesAccess,
          loading,
          error,
          niceMoney,
          niceASP,
          nicePercent,
          formatNumber,
        }}
      />
    </main>
  );
};

export default Portal;
